import { Helmet } from "react-helmet-async";

const SITE = "https://lineupsync.club";

export type Locale = "en" | "de" | "fr" | "es" | "et" | "fi";

const OG_LOCALES: Record<Locale, string> = {
  en: "en_US",
  de: "de_DE",
  fr: "fr_FR",
  es: "es_ES",
  et: "et_EE",
  fi: "fi_FI",
};

// All landing pages link to all locales reciprocally (Google requirement).
const LANDING_PATHS: Record<Locale, string> = {
  en: "/",
  de: "/de",
  fr: "/fr",
  es: "/es",
  et: "/et",
  fi: "/fi",
};

interface SeoProps {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** When set, emits hreflang alternates linking the 4 localized landing pages. */
  locale?: Locale;
  /** Absolute or site-relative URL for og:image. Defaults to brand image. */
  image?: string;
}

/**
 * Per-route head: unique title, meta description, self-referencing canonical,
 * and og:* social preview. Optional JSON-LD payload for rich results.
 */
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;

const Seo = ({ title, description, path, noindex, jsonLd, locale, image }: SeoProps) => {
  const url = `${SITE}${path}`;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE}${image.startsWith("/") ? "" : "/"}${image}`
    : DEFAULT_OG_IMAGE;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="LineupSync" />
      <meta property="og:image" content={ogImage} />
      {locale && <meta property="og:locale" content={OG_LOCALES[locale]} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {locale && (
        <>
          {(Object.keys(LANDING_PATHS) as Locale[]).map((l) => (
            <link
              key={l}
              rel="alternate"
              hrefLang={l}
              href={`${SITE}${LANDING_PATHS[l]}`}
            />
          ))}
          <link rel="alternate" hrefLang="x-default" href={`${SITE}/`} />
        </>
      )}
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;